import React, { useEffect, useRef } from 'react';
import { TRebook, TConsultant, TPageSelectionState, PAGE_SELECTION_STATE } from './types';
import { FRONTEND_SERVER, SERVER_URL } from '../../../config';
import { formatDisplayDate } from '../../../utils';
import { RebooksCellDetails } from './RebooksCellDetails';
import Tooltip from '../../common/Tooltip';

const MAX_VISIBLE_CONSULTANTS = 4;

const ConsultantsCell = ({
    consultants,
    overlayId,
}: {
    consultants: TConsultant[];
    overlayId: string;
}) => {
    if (!consultants.length) {
        return <span>-</span>;
    }

    const visible = consultants.slice(0, MAX_VISIBLE_CONSULTANTS);
    const visibleLabel = visible.map(consultant => consultant.full_name).join(', ');
    const extraCount = consultants.length - visible.length;
    const label = extraCount > 0 ? `${visibleLabel} +${extraCount}` : visibleLabel;

    if (extraCount <= 0) {
        return <span className="rebooks-consultants-cell">{label}</span>;
    }

    const tooltip = (
        <div className="rebooks-consultants-tooltip">
            {consultants.map(consultant => (
                <div key={consultant.id}>{consultant.full_name}</div>
            ))}
        </div>
    );

    return (
        <Tooltip tooltip={tooltip} overlayId={overlayId}>
            <span className="rebooks-consultants-cell rebooks-consultants-cell--truncated">
                {label}
            </span>
        </Tooltip>
    );
};

type TColumnsOptions = {
    isRebookSelected: (rebook: TRebook) => boolean;
    isRebookSelectable: (rebook: TRebook) => boolean;
    toggleRebook: (rebook: TRebook) => void;
    togglePageSelection: () => void;
    pageSelectionState: TPageSelectionState;
    hasSelectableOnPage: boolean;
    isRangeExtendable: boolean;
    openAvailabilityModal: (rebook: TRebook) => void;
};

const SelectAllHeader = ({
    state,
    onToggle,
    disabled,
    isRangeExtendable,
}: {
    state: TPageSelectionState;
    onToggle: () => void;
    disabled: boolean;
    isRangeExtendable: boolean;
}) => {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = state === PAGE_SELECTION_STATE.SOME;
        }
    }, [state]);

    let title = 'No selectable rebooks on this page';
    if (!disabled) {
        title = 'Select all selectable rebooks on this page';
    } else if (!isRangeExtendable) {
        title = 'Only the current week and up to 2 weeks back can be extended';
    }

    return (
        <input
            ref={ref}
            className="checkbox-purple"
            type="checkbox"
            checked={state === PAGE_SELECTION_STATE.ALL}
            onChange={onToggle}
            onClick={e => e.stopPropagation()}
            disabled={disabled}
            title={title}
        />
    );
};

export const createRebooksColumns = (
    allowedSortColumns: Set<string>,
    {
        isRebookSelected,
        isRebookSelectable,
        toggleRebook,
        togglePageSelection,
        pageSelectionState,
        hasSelectableOnPage,
        isRangeExtendable,
        openAvailabilityModal,
    }: TColumnsOptions,
) => [
    {
        Header: () => (
            <SelectAllHeader
                state={pageSelectionState}
                onToggle={togglePageSelection}
                disabled={!hasSelectableOnPage}
                isRangeExtendable={isRangeExtendable}
            />
        ),
        id: 'selection',
        sortable: false,
        width: 40,
        headerClassName: 'rebooks-selection-cell',
        className: 'rebooks-selection-cell',
        Cell: ({ original }: { original: TRebook }) => {
            const selectable = isRebookSelectable(original);

            let title: string | undefined;
            if (!selectable) {
                if (!isRangeExtendable) {
                    title = 'Only the current week and up to 2 weeks back can be extended';
                } else {
                    title = 'No free bookings next week';
                }
            }

            return (
                <input
                    className="checkbox-purple"
                    type="checkbox"
                    checked={selectable && isRebookSelected(original)}
                    onChange={() => toggleRebook(original)}
                    onClick={e => e.stopPropagation()}
                    disabled={!selectable}
                    title={title}
                />
            );
        },
    },
    {
        Header: 'Worker',
        id: 'worker_name',
        accessor: (row: TRebook) => row.freelancer.last_name,
        sortable: allowedSortColumns.has('worker_name'),
        Cell: ({ original }: { original: TRebook }) => {
            const { freelancer } = original;
            const workerImg = freelancer.photo_thumbnail_large
                ? freelancer.photo_thumbnail_large
                : '/static/images/default_profile.png';
            return (
                <div className="rebooks-avatar-cell">
                    <a
                        className="group-link"
                        href={workerImg}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <div
                            className="avatar"
                            style={{ backgroundImage: `url(${workerImg})` }}
                        />
                    </a>
                    <RebooksCellDetails
                        title={`${freelancer.first_name} ${freelancer.last_name}`}
                        subtitle={
                            <a
                                className="group-link"
                                href={`${FRONTEND_SERVER}/operations/freelancer/${freelancer.id}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {freelancer.reference_number}
                            </a>
                        }
                    />
                </div>
            );
        },
    },
    {
        Header: 'Job',
        id: 'job_title',
        accessor: (row: TRebook) => row.job.title,
        sortable: allowedSortColumns.has('job_title'),
        Cell: ({ original }: { original: TRebook }) => {
            const { job } = original;
            return (
                <RebooksCellDetails
                    title={job.title}
                    subtitle={
                        <a
                            className="group-link"
                            href={`${SERVER_URL}/job/${job.id}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {job.reference_number}
                        </a>
                    }
                />
            );
        },
    },
    {
        Header: 'Client',
        id: 'client_name',
        accessor: (row: TRebook) => row.client.company_name,
        sortable: allowedSortColumns.has('client_name'),
        Cell: ({ original }: { original: TRebook }) => {
            const { client } = original;
            return (
                <RebooksCellDetails
                    title={client.company_name}
                    subtitle={`${client.first_name} ${client.last_name}`}
                />
            );
        },
    },
    {
        Header: 'Consultants',
        id: 'consultants',
        sortable: false,
        minWidth: 200,
        Cell: ({ original }: { original: TRebook }) => (
            <ConsultantsCell
                consultants={original.consultants || []}
                overlayId={`rebooks-consultants-${original.id}`}
            />
        ),
    },
    {
        Header: 'Current end date',
        id: 'end_date',
        accessor: (row: TRebook) => row.end_date,
        sortable: allowedSortColumns.has('end_date'),
        Cell: ({ value }: { value: string }) => (
            <span>{formatDisplayDate(value)}</span>
        ),
    },
    {
        Header: 'Ongoing',
        id: 'is_series',
        accessor: (row: TRebook) => row.is_series,
        sortable: false,
        Cell: ({ value }: { value: boolean }) => (
            <span>{value ? 'Yes' : 'No'}</span>
        ),
    },
    {
        Header: 'Next Week Availability',
        id: 'next_week_availability',
        sortable: false,
        Cell: ({ original }: { original: TRebook }) => {
            const hasBusy = (original.busy_bookings?.length ?? 0) > 0;
            const hasFree = (original.free_bookings?.length ?? 0) > 0;
            if (hasBusy && hasFree) {
                return (
                    <button
                        type="button"
                        className="rebooks-availability-trigger bullet bullet-left bullet-status-partial-busy"
                        onClick={() => openAvailabilityModal(original)}
                    >
                        Partial Busy
                    </button>
                );
            }
            if (hasBusy) {
                return (
                    <button
                        type="button"
                        className="rebooks-availability-trigger bullet bullet-left bullet-status-OP"
                        onClick={() => openAvailabilityModal(original)}
                    >
                        Busy
                    </button>
                );
            }
            return (
                <button
                    type="button"
                    className="rebooks-availability-trigger bullet bullet-left bullet-status-CP"
                    onClick={() => openAvailabilityModal(original)}
                >
                    Free
                </button>
            );
        },
    },
];
